import React, { useMemo } from 'react';
import { Section, Chapter } from '@/types';
import { formatTime } from '@/utils/format';

interface AudioPlayerProps {
  chapter: Chapter | null;
  bookTitle: string;
  translationLabel?: string; // e.g. "Classic" or "Modern" when in both mode
  sectionText: string;
  showTranscript: boolean;
  onToggleTranscript: () => void;
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  playbackRate: number;
  trackList: Section[];
  currentTrackIndex: number;
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (volume: number) => void;
  onPlaybackRateChange: (rate: number) => void;
  onNext: () => void;
  onPrevious: () => void;
  onSegmentClick: (index: number) => void;
}

const RATES = [0.75, 1, 1.25, 1.5, 2];

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  chapter,
  bookTitle,
  translationLabel,
  sectionText,
  showTranscript,
  onToggleTranscript,
  isPlaying,
  progress,
  duration,
  volume,
  playbackRate,
  trackList,
  currentTrackIndex,
  onPlay,
  onPause,
  onSeek,
  onVolumeChange,
  onPlaybackRateChange,
  onNext,
  onPrevious,
  onSegmentClick,
}) => {
  const { totalDuration, segmentOffsets } = useMemo(() => {
    let total = 0;
    const offsets: number[] = [];
    for (const section of trackList) {
      offsets.push(total);
      total += section.duration_seconds;
    }
    return { totalDuration: total, segmentOffsets: offsets };
  }, [trackList]);

  if (trackList.length === 0) return null;

  const completedDuration = segmentOffsets[currentTrackIndex] ?? 0;
  const overallProgress = completedDuration + progress;

  const handleSkipBack = () => onSeek(Math.max(0, progress - 15));
  const handleSkipForward = () => onSeek(Math.min(duration, progress + 15));

  const cycleRate = () => {
    const idx = RATES.indexOf(playbackRate);
    onPlaybackRateChange(RATES[(idx + 1) % RATES.length]);
  };

  return (
    <div className="sp-player">
      {/* ── Segmented progress bar ── */}
      <div className="flex h-1">
        {trackList.map((section, index) => {
          const widthPercent = totalDuration > 0
            ? (section.duration_seconds / totalDuration) * 100
            : 100 / trackList.length;

          let fillPercent = 0;
          if (index < currentTrackIndex) {
            fillPercent = 100;
          } else if (index === currentTrackIndex && duration > 0) {
            fillPercent = (progress / duration) * 100;
          }

          return (
            <button
              key={section.id}
              onClick={() => onSegmentClick(index)}
              className="relative h-full cursor-pointer group"
              style={{
                width: `${widthPercent}%`,
                marginRight: index < trackList.length - 1 ? '2px' : '0',
              }}
              aria-label={`Jump to section ${index + 1}`}
            >
              <div className="h-full rounded-[1px] overflow-hidden" style={{ background: '#404040' }}>
                <div
                  className="h-full transition-all duration-200"
                  style={{
                    width: `${fillPercent}%`,
                    background: 'var(--accent-primary)',
                  }}
                />
              </div>
              {/* Hover tooltip */}
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" style={{ background: '#333' }}>
                Section {index + 1}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Main controls row ── */}
      <div className="px-4 py-2.5">
        <div className="flex items-center max-w-7xl mx-auto gap-4">

          {/* Left: now-playing info */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded hidden sm:flex items-center justify-center flex-shrink-0" style={{ background: '#282828' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#a7a7a7">
                <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55C7.79 13 6 14.79 6 17s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {chapter?.title || 'Unknown Chapter'}
                {translationLabel && (
                  <span className="ml-2 text-[11px] font-normal px-1.5 py-0.5 rounded" style={{ background: '#383838', color: 'var(--accent-primary)' }}>
                    {translationLabel}
                  </span>
                )}
              </p>
              <p className="text-[11px] truncate" style={{ color: '#a7a7a7' }}>
                {bookTitle}
              </p>
            </div>
          </div>

          {/* Center: transport controls */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Transcript toggle */}
            {sectionText && (
              <button
                onClick={onToggleTranscript}
                className={`sp-control-btn w-8 h-8 ${showTranscript ? 'text-[var(--accent-primary)]' : ''}`}
                aria-label={showTranscript ? 'Hide transcript' : 'Show transcript'}
                title={showTranscript ? 'Hide transcript' : 'Show transcript'}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z"/>
                </svg>
              </button>
            )}

            {/* Playback speed */}
            <button
              onClick={cycleRate}
              className="sp-control-btn w-8 h-8 text-[11px] font-bold"
              aria-label={`Playback speed ${playbackRate}x`}
              title={`Speed: ${playbackRate}x`}
            >
              {playbackRate}x
            </button>

            {/* Skip back 15s */}
            <button
              onClick={handleSkipBack}
              className="sp-control-btn w-8 h-8 relative"
              aria-label="Rewind 15 seconds"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="absolute">
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
              </svg>
              <span className="relative text-[7px] font-bold leading-none mt-0.5">15</span>
            </button>

            {/* Previous */}
            <button onClick={onPrevious} className="sp-control-btn w-8 h-8" aria-label="Previous section">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M3.3 1a.7.7 0 0 1 .7.7v5.15l9.95-5.744a.7.7 0 0 1 1.05.606v12.575a.7.7 0 0 1-1.05.607L4 9.149V14.3a.7.7 0 0 1-.7.7H2.7a.7.7 0 0 1-.7-.7V1.7a.7.7 0 0 1 .7-.7h.6z"/>
              </svg>
            </button>

            {/* Play / Pause */}
            <button
              onClick={isPlaying ? onPause : onPlay}
              className="sp-play-btn"
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="#000">
                  <path d="M2.7 1a.7.7 0 0 0-.7.7v12.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7H2.7zm8 0a.7.7 0 0 0-.7.7v12.6a.7.7 0 0 0 .7.7h2.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-2.6z"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="#000">
                  <path d="M3 1.713a.7.7 0 0 1 1.05-.607l10.89 6.288a.7.7 0 0 1 0 1.212L4.05 14.894A.7.7 0 0 1 3 14.288V1.713z"/>
                </svg>
              )}
            </button>

            {/* Next */}
            <button onClick={onNext} className="sp-control-btn w-8 h-8" aria-label="Next section">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M12.7 1a.7.7 0 0 0-.7.7v5.15L2.05 1.107A.7.7 0 0 0 1 1.713v12.575a.7.7 0 0 0 1.05.607L12 9.149V14.3a.7.7 0 0 0 .7.7h.6a.7.7 0 0 0 .7-.7V1.7a.7.7 0 0 0-.7-.7h-.6z"/>
              </svg>
            </button>

            {/* Skip forward 15s */}
            <button
              onClick={handleSkipForward}
              className="sp-control-btn w-8 h-8 relative"
              aria-label="Forward 15 seconds"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="absolute">
                <path d="M12 5V1l5 5-5 5V7c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6h2c0 4.42-3.58 8-8 8s-8-3.58-8-8 3.58-8 8-8z"/>
              </svg>
              <span className="relative text-[7px] font-bold leading-none mt-0.5">15</span>
            </button>
          </div>

          {/* Right: volume + time */}
          <div className="flex items-center gap-3 flex-1 justify-end">
            {/* Volume */}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => onVolumeChange(volume > 0 ? 0 : 1)}
                className="sp-control-btn w-7 h-7"
                aria-label={volume > 0 ? 'Mute' : 'Unmute'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  {volume === 0 ? (
                    <path d="M13.86 5.47a.75.75 0 0 0-1.061 0l-1.47 1.47-1.47-1.47A.75.75 0 0 0 8.8 6.53L10.269 8l-1.47 1.47a.75.75 0 1 0 1.06 1.06l1.47-1.47 1.47 1.47a.75.75 0 0 0 1.06-1.06L12.39 8l1.47-1.47a.75.75 0 0 0 0-1.06zM10.116 1.5A.75.75 0 0 0 8.991.85l-3.5 2.303a.75.75 0 0 0-.491.7v6.294a.75.75 0 0 0 .491.7l3.5 2.303a.75.75 0 0 0 1.125-.65V2.15z"/>
                  ) : volume < 0.5 ? (
                    <path d="M9.741.85a.75.75 0 0 1 .375.65v13a.75.75 0 0 1-1.125.65l-6.925-4.557a1.5 1.5 0 0 1-.691-1.262V6.67a1.5 1.5 0 0 1 .691-1.262L9.016.85a.75.75 0 0 1 .725 0zm1.53 3.68a.75.75 0 0 1 1.06 0 4.5 4.5 0 0 1 0 6.94.75.75 0 1 1-1.06-1.06 3 3 0 0 0 0-4.82.75.75 0 0 1 0-1.06z"/>
                  ) : (
                    <path d="M9.741.85a.75.75 0 0 1 .375.65v13a.75.75 0 0 1-1.125.65l-6.925-4.557a1.5 1.5 0 0 1-.691-1.262V6.67a1.5 1.5 0 0 1 .691-1.262L9.016.85a.75.75 0 0 1 .725 0zm1.53 3.68a.75.75 0 0 1 1.06 0 4.5 4.5 0 0 1 0 6.94.75.75 0 1 1-1.06-1.06 3 3 0 0 0 0-4.82.75.75 0 0 1 0-1.06zm2.12-2.12a.75.75 0 0 1 1.06 0 7.5 7.5 0 0 1 0 11.18.75.75 0 1 1-1.06-1.06 6 6 0 0 0 0-9.06.75.75 0 0 1 0-1.06z"/>
                  )}
                </svg>
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                className="sp-range w-20"
                aria-label="Volume"
              />
            </div>

            {/* Time display */}
            <span className="text-[11px] font-mono tabular-nums whitespace-nowrap" style={{ color: '#a7a7a7' }}>
              {formatTime(overallProgress)} / {formatTime(totalDuration)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
